import React, { useState, useCallback } from "react"
import { Gesture, GestureDetector } from "react-native-gesture-handler"
import Animated, {
  cancelAnimation,
  clamp,
  Easing,
  runOnJS,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from "react-native-reanimated"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import styled from "styled-components/native"

import tokens from "@treefort/tokens/app"

import config from "../../config"
import { useTracks } from "../../hooks/audio"
import useAppManifest from "../../hooks/use-app-manifest"
import { useBottomTabsHeight } from "../../hooks/use-bottom-tabs-height"
import { useNavigate } from "../../hooks/use-navigate"
import useWindowDimensions from "../../hooks/use-window-dimensions"
import analytics from "../../lib/analytics"
import { Track } from "../../lib/audio-player"
import { shareUrl } from "../../lib/share-url"
import {
  getPathFromTrack,
  getRouteFromPath,
  NO_TAB,
  SEARCH_TAB,
} from "../../navigation/routes"
import { FullscreenAudioPlayer } from "../audio-player-fullscreen"
import InlineAudioPlayer from "../audio-player-inline"
import BottomTabBar from "../bottom-tab-bar"
import LinearGradient from "../linear-gradient"
import Onboarding from "../onboarding"

const FULLSCREEN_AUDIO_PLAYER_ANIMATION = {
  duration: 700,
  easing: Easing.out(Easing.exp),
}

const Container = styled.View`
  background: ${({ theme }) => theme.colors.background.primary};
`

const FullscreenAudioPlayerContainer = styled(Animated.View)<{
  $height: number
  $paddingTop: number
  $paddingBottom: number
}>`
  flex-direction: column;
  align-items: center;
  position: absolute;
  top: 0px;
  width: 100%;
  height: ${(props) => props.$height}px;
  padding-top: ${(props) => props.$paddingTop}px;
  padding-bottom: ${(props) => props.$paddingBottom}px;
  background: ${({ theme }) => theme.colors.background.primary};
`

const FullscreenAudioPlayerDropShadow = styled(LinearGradient)`
  position: absolute;
  top: ${(props) => -props.theme.audioPlayerFullscreen.dropShadowHeight}px;
  height: ${(props) => props.theme.audioPlayerFullscreen.dropShadowHeight}px;
  width: 100%;
`

export default function AppFooterMobile(
  props: Omit<Parameters<typeof BottomTabBar>[0], "height" | "safeAreaInsets">,
): React.JSX.Element | null {
  const tracks = useTracks()
  const showAudioPlayer = tracks.length > 0
  const safeAreaInsets = useSafeAreaInsets()
  const { height: windowHeight } = useWindowDimensions()
  const [fullscreenAudioPlayerOpen, setFullscreenAudioPlayerOpen] =
    useState(false)
  const navigate = useNavigate()
  const bottomTabsHeight = useBottomTabsHeight()
  const manifest = useAppManifest()

  const footerHeightWithInlineAudioPlayer =
    bottomTabsHeight + safeAreaInsets.bottom + tokens.audioPlayerInline.height
  const fullscreenAudioPlayerMaxPanDistance =
    windowHeight - footerHeightWithInlineAudioPlayer
  const fullscreenAudioPlayerStartY = useSharedValue(0)
  const fullscreenAudioPlayerTranslateY = useSharedValue(0)
  const footerTranslateY = useDerivedValue(() => {
    return (
      -fullscreenAudioPlayerTranslateY.value *
      (footerHeightWithInlineAudioPlayer / fullscreenAudioPlayerMaxPanDistance)
    )
  })

  const openFullscreenAudioPlayer = useCallback(() => {
    setFullscreenAudioPlayerOpen(true)
    fullscreenAudioPlayerTranslateY.value = withTiming(
      -fullscreenAudioPlayerMaxPanDistance,
      FULLSCREEN_AUDIO_PLAYER_ANIMATION,
    )
  }, [fullscreenAudioPlayerTranslateY, fullscreenAudioPlayerMaxPanDistance])

  const closeFullscreenAudioPlayer = useCallback(() => {
    fullscreenAudioPlayerTranslateY.value = withTiming(
      0,
      FULLSCREEN_AUDIO_PLAYER_ANIMATION,
      (completed) => {
        if (completed) {
          runOnJS(setFullscreenAudioPlayerOpen)(false)
        }
      },
    )
  }, [fullscreenAudioPlayerTranslateY])

  const onPressAudioPlayerTitle = useCallback(
    (track: Track) => {
      const path = getPathFromTrack(track, NO_TAB)
      navigate(path, undefined, "push")
      closeFullscreenAudioPlayer()
    },
    [navigate, closeFullscreenAudioPlayer],
  )

  const fullscreenAudioPlayerPanGesture = Gesture.Pan()
    .onStart(() => {
      cancelAnimation(fullscreenAudioPlayerTranslateY)
      fullscreenAudioPlayerStartY.value = fullscreenAudioPlayerTranslateY.value
    })
    .onUpdate((event) => {
      fullscreenAudioPlayerTranslateY.value = clamp(
        fullscreenAudioPlayerStartY.value + event.translationY,
        -fullscreenAudioPlayerMaxPanDistance,
        0,
      )
    })
    .onEnd((event) => {
      // If the user dragged at least 10% of the way closed, finish collapsing
      // for them. Otherwise bounce back to the open state
      if (event.translationY / fullscreenAudioPlayerMaxPanDistance > 0.1) {
        runOnJS(closeFullscreenAudioPlayer)()
      } else {
        runOnJS(openFullscreenAudioPlayer)()
      }
    })

  const fullscreenAudioPlayerAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: fullscreenAudioPlayerTranslateY.value }],
  }))

  const footerAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: footerTranslateY.value }],
  }))

  return (
    <Container>
      {showAudioPlayer ? (
        <GestureDetector gesture={fullscreenAudioPlayerPanGesture}>
          <FullscreenAudioPlayerContainer
            $height={windowHeight}
            $paddingTop={safeAreaInsets.top}
            $paddingBottom={safeAreaInsets.bottom}
            style={fullscreenAudioPlayerAnimatedStyle}
            pointerEvents={!fullscreenAudioPlayerOpen ? "none" : undefined}
          >
            <FullscreenAudioPlayerDropShadow
              fadeTowards="top"
              color={tokens.audioPlayerFullscreen.dropShadowColor}
            />
            <FullscreenAudioPlayer
              open={fullscreenAudioPlayerOpen}
              onClose={closeFullscreenAudioPlayer}
              onPressTitle={onPressAudioPlayerTitle}
              onShare={async (track: Track) => {
                const path = getPathFromTrack(track, SEARCH_TAB)
                const wasShareSuccessful = await shareUrl({
                  url: `https://${config.DOMAIN_NAME}${path}`,
                })
                if (wasShareSuccessful) {
                  const route = getRouteFromPath(path, manifest)
                  analytics.logShare(route, manifest)
                }
              }}
            />
          </FullscreenAudioPlayerContainer>
        </GestureDetector>
      ) : null}
      <Animated.View style={footerAnimatedStyle}>
        {showAudioPlayer ? (
          <InlineAudioPlayer
            onPress={openFullscreenAudioPlayer}
            safeAreaInsets={
              bottomTabsHeight > 0
                ? { ...safeAreaInsets, bottom: 0 }
                : safeAreaInsets
            }
          />
        ) : null}
        {bottomTabsHeight > 0 ? (
          <BottomTabBar
            {...props}
            height={bottomTabsHeight}
            safeAreaInsets={safeAreaInsets}
          />
        ) : null}
      </Animated.View>
      <Onboarding />
    </Container>
  )
}
